// ===============================
// pages/Home.jsx
// FULLY DYNAMIC HOME PAGE
// ===============================

import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CartContext } from "../../context/CartContext";
import "./Home.css";
import rakhi from "../../assets/rakhi.jpg";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const PRODUCT_API = `${API_BASE_URL}/api/products`;
const CATEGORY_API = `${API_BASE_URL}/api/categories`;
const FALLBACK_IMAGE = "https://via.placeholder.com/400x400?text=No+Image";

const Home = () => {
  const { addToCart } = useContext(CartContext);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const resolveImageUrl = (img) => {
    if (!img) return FALLBACK_IMAGE;

    // 1. If it's already a full web URL (Cloudinary, etc.) or a Base64 string, return it as-is
    if (img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:")) {
      return img;
    }

    // 2. Clean up the local upload path string
    let cleanPath = img;
    if (cleanPath.startsWith("/uploads/")) {
      cleanPath = cleanPath.replace("/uploads/", "");
    } else if (cleanPath.startsWith("uploads/")) {
      cleanPath = cleanPath.replace("uploads/", "");
    }

    // 3. Remove any trailing slash from your API_BASE_URL just in case
    const baseUrl = API_BASE_URL.endsWith("/") 
      ? API_BASE_URL.slice(0, -1) 
      : API_BASE_URL;

    // 4. Return the full path
    return `${baseUrl}/uploads/${cleanPath}`;
  };

  const getProductImage = (product) => {
    if (product?.image) return resolveImageUrl(product.image);

    if (Array.isArray(product?.images) && product.images.length > 0) {
      return resolveImageUrl(product.images[0]);
    }

    return FALLBACK_IMAGE;
  };

  const getCategoryImage = (category) => {
    if (category?.image) return resolveImageUrl(category.image);
    return FALLBACK_IMAGE;
  };

  const fetchHomeData = async () => {
    try {
      setLoadingProducts(true);
      setLoadingCategories(true);

      const [productRes, categoryRes] = await Promise.all([
        axios.get(PRODUCT_API),
        axios.get(CATEGORY_API),
      ]);

      let allProducts = [];
      let allCategories = [];

      if (Array.isArray(productRes.data)) {
        allProducts = productRes.data;
      } else if (Array.isArray(productRes.data.products)) {
        allProducts = productRes.data.products;
      } else if (Array.isArray(productRes.data.data)) {
        allProducts = productRes.data.data;
      }

      if (Array.isArray(categoryRes.data)) {
        allCategories = categoryRes.data;
      } else if (Array.isArray(categoryRes.data.categories)) {
        allCategories = categoryRes.data.categories;
      } else if (Array.isArray(categoryRes.data.data)) {
        allCategories = categoryRes.data.data;
      }

      // CRITICAL FIX: Filter out inactive products so they never show up on the homepage!
      const activeProducts = allProducts.filter((item) => item.isActive !== false);

      setProducts(activeProducts.slice(0, 8));
      setCategories(allCategories.filter((item) => item.isActive !== false));
    } catch (error) {
      console.error("Home page fetch error:", error);
      setProducts([]);
      setCategories([]);
    } finally {
      setLoadingProducts(false);
      setLoadingCategories(false);
    }
  };

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="container hero-content">
          <div className="hero-text">
            <span>Raksha Bandhan Collection 2026</span>
            <h1>Make Raksha Bandhan Extra Special ❤️</h1>
            <p>
              Celebrate the beautiful bond of love with personalised mugs, cushions,
              gift hampers and thoughtful gifts made especially for your brother or sister.
            </p>
            <Link to="/shop">
              <button>Shop Raksha Bandhan Gifts</button>
            </Link>
          </div>

          <div className="hero-image">
            <img src={rakhi} alt="hero" />
          </div>
        </div>
      </section>

      <section className="categories-section">
        <div className="container">
          <div className="section-title">
            <h2>Shop By Categories</h2>
            <p>Explore trending collections</p>
          </div>

          {loadingCategories ? (
            <div className="loading-box">
              <h2>Loading Categories...</h2>
            </div>
          ) : categories.length === 0 ? (
            <div className="loading-box">
              <h2>No categories found.</h2>
            </div>
          ) : (
            <div className="categories-grid">
              {categories.map((item) => (
                <Link
                  to={`/shop?category=${item._id || item.id}`}
                  className="category-card"
                  key={item._id || item.id}
                >
                  <img src={getCategoryImage(item)} alt={item.name} />
                  <div className="category-overlay">
                    <h3>{item.name}</h3>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="products-section">
        <div className="container">
          <div className="section-title">
            <h2>Featured Products</h2>
            <p>Best selling products</p>
          </div>

          {loadingProducts ? (
            <div className="loading-box">
              <h2>Loading Products...</h2>
            </div>
          ) : products.length === 0 ? (
            <div className="loading-box">
              <h2>No products found.</h2>
            </div>
          ) : (
            <div className="products-grid">
              {products.map((product) => (
                <div className="product-card" key={product._id}>
                  <Link to={`/product/${product._id}`} className="product-link">
                    <div className="product-image">
                      <img src={getProductImage(product)} alt={product.name} />
                    </div>

                    <div className="product-info">
                      <span className="home-category">
                        {product.category?.name || product.category || "No Category"}
                      </span>
                      <h3>{product.name}</h3>
                      <p className="home-price">₹{product.price}</p>
                    </div>
                  </Link>

                  <div className="home-btn-area">
                    <button
                      onClick={() =>
                        addToCart({
                          ...product,
                          quantity: 1,
                        })
                      }
                    >
                      Add To Cart
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="view-all-btn">
            <Link to="/shop">
              <button>View All Products</button>
            </Link>
          </div>
        </div>
      </section>

      <section className="offer-banner">
        <div className="container offer-content">
          <div>
            <h2>Big Sale Up To 50% OFF</h2>
            <p>Grab your favorite products today with exclusive deals.</p>
          </div>
          <Link to="/deals">
            <button>Explore Deals</button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;